import clsx from 'clsx';
import { ArrowLeft, ArrowRight, BookOpen, CircleHelp, ExternalLink, FolderOpen, Globe, House, LogOut, Menu, NotebookText, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useLocation, useNavigate, useNavigationType } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { setLanguage } from '../i18n';
import { locate, routeMatches, sumBadge, useNav, visibleTabs } from '../lib/nav';
import { authCall } from '../lib/rpc';
import { useMe } from '../lib/me';
import { scrollPositions } from '../lib/sticky';
import { Avatar } from './avatar';
import { AppMark, Brand, useBranding } from './brand';
import { DemoBar } from './DemoBar';
import { BellButton, NotificationsProvider } from './notifications';
import { SectionTabBar } from './section';
import { SectionSearch } from './section-search';
import { ThemeToggle } from './theme';

/** Language names are always written in their own language (not translated), so the buttons are readable for everybody. */
const LANGUAGE_NAMES = { de: 'Deutsch', en: 'English', tr: 'Türkçe' } as const;

/** Last tab of a section that was open: the menu entry leads back to it. */
const lastTab = new Map<string, string>();

/**
 * Where the person is in the history of the app: back and forward buttons for people who do not use the browser
 * buttons or key combinations (and for the installed app window). The router stores the position of the entry in
 * the history state; the highest position seen tells whether there is a page to go forward to.
 */
function useHistoryButtons() {
  const location = useLocation();
  const type = useNavigationType();
  const furthest = useRef(0);
  const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
  if (type === 'PUSH') furthest.current = idx;
  else furthest.current = Math.max(furthest.current, idx);
  void location;
  return { canBack: idx > 0, canForward: idx < furthest.current };
}

/**
 * Frame of the application: menu with sections (rights decide what is shown), top bar (back/forward, where am I,
 * notifications), tab bar of the section, scrolling page. The page remembers its scroll position for "back".
 */
export function Layout() {
  const { t, i18n } = useTranslation();
  const { me, isLead } = useMe();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const navType = useNavigationType();
  const qc = useQueryClient();
  const brand = useBranding().data;
  const { groups } = useNav();
  const { canBack, canForward } = useHistoryButtons();
  const mainRef = useRef<HTMLElement>(null);
  const lastPath = useRef(location.pathname);

  const here = locate(groups, location.pathname);
  useEffect(() => {
    if (here) lastTab.set(here.section.id, here.tab.to);
  }, [here]);
  useEffect(() => setOpen(false), [location.pathname]);

  // title of the page: section name, or the page outside the menu
  const outside = routeMatches('/profile', location.pathname) ? t('profile.title') : routeMatches('/help', location.pathname) ? t('nav.help') : null;
  const title = here?.section.label ?? outside ?? brand?.appName ?? t('app.name');
  useEffect(() => {
    const tab = here && visibleTabs(here.section).length > 1 ? ` · ${here.tab.label}` : '';
    document.title = `${title}${tab} – ${brand?.appName || t('app.name')}`;
  }, [title, here, brand?.appName, t]);

  // scroll position: remember per history entry, restore when going back, start at the top for a new page
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    let raf = 0;
    const key = location.key;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => scrollPositions.set(key, el.scrollTop));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [location.key]);
  useLayoutEffect(() => {
    const el = mainRef.current;
    const samePage = lastPath.current === location.pathname;
    lastPath.current = location.pathname;
    if (!el) return;
    if (navType === 'POP') {
      const top = scrollPositions.get(location.key) ?? 0;
      if (!top) {
        el.scrollTop = 0;
        return;
      }
      // the content of the page arrives a moment later: try until the page is long enough
      let tries = 0;
      let timer = 0;
      const apply = () => {
        el.scrollTop = top;
        if (Math.abs(el.scrollTop - top) > 2 && tries++ < 20) timer = window.setTimeout(apply, 60);
      };
      apply();
      return () => window.clearTimeout(timer);
    }
    // a change of a filter in the address (same page) keeps the position; a new page starts at the top
    if (navType === 'PUSH' && !samePage) el.scrollTop = 0;
  }, [location.key, location.pathname, navType]);

  const logout = async () => {
    try {
      await authCall('logout');
    } finally {
      qc.clear();
      window.location.hash = '#/login';
      window.location.reload();
    }
  };

  const icon = 'size-4 shrink-0';
  const nav = (
    <nav className="flex h-full flex-col px-3 py-4" aria-label={t('nav.menu')}>
      <div className="px-2 pb-4">
        <Brand subtitle={__DEMO__ ? t('app.demo') : undefined} />
      </div>
      {/* only the list of pages scrolls; account, language and help stay in view on a short screen */}
      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
      {groups.map((g) => (
        <div key={g.id} className="flex flex-col gap-0.5">
          <div className="px-2 pb-1 text-[11px] font-semibold tracking-wide text-faint uppercase">{g.label}</div>
          {g.sections.map((s) => {
            const tabs = visibleTabs(s);
            const remembered = tabs.find((x) => x.to === lastTab.get(s.id));
            const target = (remembered ?? tabs[0]).to;
            const active = here?.section.id === s.id;
            const badge = sumBadge(s.tabs);
            const bad = s.tabs.some((x) => x.show && x.badgeTone === 'bad');
            return (
              <Link
                key={s.id}
                to={target}
                aria-current={active ? 'page' : undefined}
                className={clsx('flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm', active ? 'bg-accent-soft font-medium text-accent' : 'text-ink hover:bg-sheet-2')}
              >
                {s.icon}
                <span className="flex-1 truncate">{s.label}</span>
                {badge > 0 && (
                  <span
                    className={clsx('num rounded-full px-1.5 text-[11px] font-semibold', bad ? 'bg-bad text-sheet' : 'bg-accent text-accent-ink')}
                    title={bad ? t('nav.rejectedBadge', { count: badge }) : undefined}
                  >
                    {badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      ))}
      </div>
      <div className="mt-3 flex shrink-0 flex-col gap-2 border-t border-line pt-3">
        {me.settings.links.length > 0 && (
          // quick links of the company, always at hand above the own name (each opens the company's page in a new tab);
          // only links with an address appear, the labels can be changed in the settings
          <nav aria-label={t('shell.quickLinks')} className="flex flex-col gap-1">
            {me.settings.links.map((l) => {
              const Icon = l.key === 'standards' ? BookOpen : l.key === 'meetings' ? NotebookText : l.key === 'formaHome' ? House : l.key === 'sharepoint' ? FolderOpen : Globe;
              const label = l.label || t(`shell.links.${l.key}`);
              return (
                <a
                  key={l.key}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`${label} – ${t(`shell.linkHints.${l.key}`)}`}
                  className="flex items-center gap-2.5 rounded-md border border-line px-2 py-1.5 text-sm font-medium hover:border-accent hover:bg-accent-soft"
                >
                  <Icon className="size-4 shrink-0 text-accent" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{label}</span>
                  <ExternalLink className="size-3 shrink-0 text-faint" aria-hidden />
                </a>
              );
            })}
          </nav>
        )}
        <NavLink
          to="/profile"
          className={({ isActive }) => clsx('flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm', isActive ? 'bg-accent-soft text-accent' : 'hover:bg-sheet-2')}
        >
          <Avatar personId={me.person.id} name={me.person.displayName} avatarAt={me.person.avatarAt} size={30} />
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{me.person.displayName}</span>
            <span className="block truncate text-xs text-graphite">
              {t(`people.roles.${me.person.role as 'employee'}`)}
              {isLead() ? ` · ${t('projects.role.lead')}` : ''}
            </span>
          </span>
        </NavLink>
        <div className="flex items-center gap-1 px-2">
          {(['de', 'en', 'tr'] as const).map((l) => (
            <button
              key={l}
              onClick={() => setLanguage(l)}
              aria-pressed={i18n.language === l}
              aria-label={LANGUAGE_NAMES[l]}
              title={LANGUAGE_NAMES[l]}
              lang={l}
              className={clsx('rounded px-1.5 py-0.5 text-xs font-medium uppercase', i18n.language === l ? 'bg-ink text-sheet' : 'text-graphite hover:text-ink')}
            >
              {l}
            </button>
          ))}
          <ThemeToggle className="ml-auto" />
        </div>
        <NavLink to="/help" className={({ isActive }) => clsx('flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm', isActive ? 'bg-accent-soft text-accent' : 'text-graphite hover:bg-sheet-2 hover:text-ink')}>
          <CircleHelp className={icon} /> {t('nav.help')}
        </NavLink>
        {!__DEMO__ && (
          <button onClick={logout} className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-graphite hover:bg-sheet-2 hover:text-ink">
            <LogOut className={icon} /> {t('nav.logout')}
          </button>
        )}
      </div>
    </nav>
  );

  const navButton = 'inline-flex size-8 items-center justify-center rounded-md text-graphite hover:bg-sheet-2 hover:text-ink disabled:pointer-events-none disabled:opacity-35';

  return (
    <NotificationsProvider>
      <div className="flex h-full flex-col">
        {__DEMO__ && <DemoBar />}
        <div className="flex min-h-0 flex-1">
          <aside className="hidden w-60 shrink-0 border-r border-line bg-sheet lg:block print:hidden">{nav}</aside>
          {open && (
            <div className="fixed inset-0 z-40 lg:hidden" onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)}>
              <div className="absolute inset-0 bg-ink/30" onClick={() => setOpen(false)} />
              <aside className="absolute inset-y-0 left-0 w-72 max-w-[85vw] border-r border-line bg-sheet">
                <button className="absolute top-3 right-3 rounded p-1 text-graphite" onClick={() => setOpen(false)} aria-label={t('common.close')}>
                  <X className="size-5" />
                </button>
                {nav}
              </aside>
            </div>
          )}
          <div className="flex min-w-0 flex-1 flex-col">
            <header className="flex items-center gap-1 border-b border-line bg-sheet px-3 py-1.5 sm:px-4 print:hidden">
              <button onClick={() => setOpen(true)} className={clsx(navButton, 'lg:hidden')} aria-label={t('nav.menu')}>
                <Menu className="size-5" />
              </button>
              <button type="button" className={navButton} onClick={() => navigate(-1)} disabled={!canBack} aria-label={t('shell.back')} title={t('shell.backTitle')}>
                <ArrowLeft className="size-[18px]" />
              </button>
              <button type="button" className={navButton} onClick={() => navigate(1)} disabled={!canForward} aria-label={t('shell.forward')} title={t('shell.forwardTitle')}>
                <ArrowRight className="size-[18px]" />
              </button>
              <div className="ml-2 flex min-w-0 flex-1 items-center gap-2">
                {brand?.logo ? <img src={brand.logo} alt="" className="max-h-6 max-w-24 rounded bg-white object-contain px-1 lg:hidden" /> : <AppMark className="size-6 lg:hidden" />}
                <span className="truncate text-sm font-semibold">{title}</span>
              </div>
              <SectionSearch />
              <BellButton />
            </header>
            {here && <SectionTabBar section={here.section} activeTo={here.tab.to} />}
            <main ref={mainRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 print:overflow-visible">
              <div className="mx-auto max-w-[1400px]">
                <Outlet />
              </div>
            </main>
          </div>
        </div>
      </div>
    </NotificationsProvider>
  );
}
